import * as Sentry from '@sentry/react-native'
import { isReportableError, isTransientNetworkError } from './reportableError'

/**
 * Central error/diagnostic reporting. One place so that:
 *   - every capture is filtered through isReportableError (no noise),
 *   - tagging is consistent,
 *   - it's a single no-op when Sentry is disabled (no DSN — local dev, tests).
 *
 * Sentry imports live here (and in the few modules that call this), NOT in
 * anything the vitest/node suite imports — `@sentry/react-native` pulls in RN
 * internals that reference `__DEV__`. The decision logic is in the pure
 * `reportableError` module, which the tests import instead.
 */

type Level = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'

interface ReportContext {
  tags?: Record<string, string>
  extra?: Record<string, unknown>
}

/**
 * Capture an unexpected error. Expected/handled HTTP statuses (401/403/404/
 * 409/422) are dropped; network/timeout/5xx and malformed-request bugs are
 * sent. Transient network errors (timeout, TLS, DNS, fetch-failed) are
 * downgraded to a breadcrumb rather than a Sentry capture — they are
 * environmental and generate high-volume, low-signal issues at error level.
 * Use this anywhere an error would otherwise be silently trapped.
 */
export function reportError(err: unknown, context: ReportContext = {}): void {
  // Transient connectivity failures: leave a breadcrumb so we have timeline
  // context if something else fires, but don't raise a Sentry issue.
  if (isTransientNetworkError(err)) {
    Sentry.addBreadcrumb({
      category: 'network',
      message: err instanceof Error ? err.message : String(err),
      level: 'warning',
      data: context.extra,
    })
    return
  }
  if (!isReportableError(err)) return
  Sentry.captureException(err, { tags: context.tags, extra: context.extra })
}

/** Capture a synthetic diagnostic message (e.g. the WS half-open watchdog). */
export function reportMessage(
  message: string,
  context: ReportContext & { level?: Level } = {},
): void {
  Sentry.captureMessage(message, {
    level: context.level ?? 'warning',
    tags: context.tags,
    extra: context.extra,
  })
}

/**
 * Leave a breadcrumb for the timeline. Breadcrumbs are buffered locally and
 * only shipped when an event (error/message) is captured in the same session —
 * so lifecycle breadcrumbs (e.g. WS open/close) are free until something
 * actually goes wrong, then they provide the lead-up.
 */
export function addBreadcrumb(breadcrumb: {
  category?: string
  message: string
  level?: Level
  data?: Record<string, unknown>
}): void {
  Sentry.addBreadcrumb(breadcrumb)
}
